/**
 * stackKit — stacked-text layout: the math-notation foundation (CATALOG F1).
 *
 * Serves: stacked fraction (M0 proof) · mixed number · long-division bracket ·
 * column add/sub/multiply · radical vinculum (the bar law generalizes) ·
 * sized grouping brackets.
 *
 * Pure layout math only — see kits/specs.ts for the spec vocabulary and the
 * no-fabric law. All blocks are local-origin (0,0 = top-left).
 */
import type { KitBlock, LineSpec, PathSpec, PrimitiveSpec, RectSpec, TextSpec, TextMeasurer } from './specs';
import { clamp, TEXT_BOX_HEIGHT } from './specs';

// ---------------------------------------------------------------------------
// Stacked pair (fraction core)
// ---------------------------------------------------------------------------

export interface StackedPairOptions {
  /** Top slot content ('' = deliberately blank slot — fill-in fractions). */
  numerator: string;
  /** Bottom slot content ('' = blank slot). */
  denominator: string;
  fontSize: number;
  /** Rule thickness in px. */
  barWeight: number;
  color: string;
  fontFamily: string;
  measure: TextMeasurer;
  /**
   * Bar overhang beyond the widest slot, PER SIDE.
   * Default fontSize * 0.14 — enough to read as a rule, not a minus sign.
   */
  padding?: number;
  /** Vertical clearance between each text box and the bar edge. Default fontSize * 0.08. */
  gap?: number;
  /** Dblclick edit tags (recipe param names) for the two slots. */
  numeratorParam?: string;
  denominatorParam?: string;
}

export interface StackedPairBlock extends KitBlock {
  /** Vertical center of the rule — recipes align "=" signs etc. to this. */
  barY: number;
  /** The rule's length — THE BAR WIDTH LAW output (see below). */
  barWidth: number;
}

/**
 * Vertically stacked pair over a rule — the stacked-fraction construction.
 *
 * THE FRACTION BAR LAW: the bar must be exactly as long as the WIDER of the
 * two slots plus a fixed overhang each side —
 *
 *     barWidth = max(measure(numerator), measure(denominator)) + 2 * padding
 *
 * — so "1/2" gets a short bar and "13/100" a long one, and re-editing either
 * slot re-derives the bar. A fixed-length bar is the classic broken-fraction
 * look (bar wider than a one-digit fraction, clipping a three-digit one).
 *
 * BASELINE MATH: fabric's single-line text box height is fontSize * 1.13
 * (TEXT_BOX_HEIGHT — face-independent, see specs.ts). Stacking is therefore
 * exact, not eyeballed:
 *
 *     numerator box:   y = 0                      (height boxH)
 *     bar centerline:  barY = boxH + gap + barWeight/2
 *     denominator box: y = barY + barWeight/2 + gap
 *
 * Both texts center on the bar's midpoint (originX 'center'), so slots of
 * different widths stay optically stacked.
 */
export function stackedPair(opts: StackedPairOptions): StackedPairBlock {
  const fontSize = Math.max(1, opts.fontSize);
  const barWeight = clamp(opts.barWeight, 0.5, fontSize);
  const padding = opts.padding ?? fontSize * 0.14;
  const gap = opts.gap ?? fontSize * 0.08;

  const numW = opts.numerator ? opts.measure(opts.numerator, fontSize) : 0;
  const denW = opts.denominator ? opts.measure(opts.denominator, fontSize) : 0;
  // Blank BOTH slots still yields a usable rule one em long.
  const slotW = Math.max(numW, denW, fontSize * 0.6);
  const barWidth = slotW + 2 * padding;

  const boxH = fontSize * TEXT_BOX_HEIGHT;
  const barY = boxH + gap + barWeight / 2;
  const denTop = barY + barWeight / 2 + gap;
  const centerX = barWidth / 2;

  const textSpec = (text: string, y: number, param?: string): TextSpec => ({
    kind: 'text',
    text,
    x: centerX,
    y,
    fontSize,
    fontFamily: opts.fontFamily,
    fill: opts.color,
    originX: 'center',
    originY: 'top',
    ...(param ? { param } : {}),
  });

  const bar: LineSpec = {
    kind: 'line',
    x1: 0,
    y1: barY,
    x2: barWidth,
    y2: barY,
    stroke: opts.color,
    strokeWidth: barWeight,
    lineCap: 'butt',
  };

  return {
    specs: [
      textSpec(opts.numerator, 0, opts.numeratorParam),
      bar,
      textSpec(opts.denominator, denTop, opts.denominatorParam),
    ],
    width: barWidth,
    height: denTop + boxH,
    barY,
    barWidth,
  };
}

// ---------------------------------------------------------------------------
// Digit columns (column add / subtract / multiply)
// ---------------------------------------------------------------------------

export interface DigitColumnsOptions {
  /**
   * The problem, one string per row, e.g. ['347', '+ 85']. Each CHARACTER
   * gets its own column cell; rows are RIGHT-aligned (the units-column law).
   */
  rows: string[];
  fontSize: number;
  color: string;
  fontFamily: string;
  measure: TextMeasurer;
  /** Column pitch. Default = measure('0') * 1.45 — air between digit boxes. */
  colWidth?: number;
  /** Extra vertical space between rows. Default fontSize * 0.18. */
  rowGap?: number;
  /** Small boxes above every column for carry/borrow marks. */
  carrySlots?: boolean;
  /** Rule under the LAST row (the sum line) — on by default. */
  rule?: boolean;
  ruleWeight?: number;
  /** Empty answer boxes below the rule, one per column. */
  answerBoxes?: boolean;
  /** Stroke color for carry/answer boxes; defaults to `color`. */
  boxColor?: string;
}

export interface DigitColumnsBlock extends KitBlock {
  /** Center x of each column, leftmost first — the alignment law output. */
  colCenters: number[];
  columns: number;
}

/**
 * Horizontal digit-column alignment with per-column cells.
 *
 * THE UNITS-COLUMN LAW: every row is right-aligned into the same column
 * grid, so the LAST character of every row shares a column center — ones
 * under ones, tens under tens. Each character is its OWN centered text spec
 * (one text per cell), which is what makes per-column carry/borrow slots and
 * per-column answer boxes line up by construction rather than by kerning
 * luck.
 *
 * Layout (top to bottom): optional carry-slot row → digit rows → optional
 * rule under the last row → optional answer-box row.
 */
export function digitColumns(opts: DigitColumnsOptions): DigitColumnsBlock {
  const fontSize = Math.max(1, opts.fontSize);
  const rows = opts.rows.filter((r) => r.length > 0);
  const columns = Math.max(1, ...rows.map((r) => r.length));
  const colWidth = opts.colWidth ?? opts.measure('0', fontSize) * 1.45;
  const rowGap = opts.rowGap ?? fontSize * 0.18;
  const boxH = fontSize * TEXT_BOX_HEIGHT;
  const boxColor = opts.boxColor ?? opts.color;
  const rule = opts.rule !== false;
  const ruleWeight = opts.ruleWeight ?? Math.max(2, fontSize * 0.06);

  const colCenters = Array.from({ length: columns }, (_, i) => (i + 0.5) * colWidth);
  const width = columns * colWidth;
  const specs: PrimitiveSpec[] = [];

  let y = 0;

  // Carry/borrow slots: a small dashed box centered over every column.
  if (opts.carrySlots) {
    const slotH = fontSize * 0.5;
    const slotW = colWidth * 0.62;
    for (let c = 0; c < columns; c++) {
      const slot: RectSpec = {
        kind: 'rect',
        x: colCenters[c] - slotW / 2,
        y,
        width: slotW,
        height: slotH,
        fill: '',
        stroke: boxColor,
        strokeWidth: Math.max(1, fontSize * 0.02),
        dash: [fontSize * 0.07, fontSize * 0.06],
        rx: slotH * 0.16,
      };
      specs.push(slot);
    }
    y += slotH + fontSize * 0.14;
  }

  // Digit rows, right-aligned into the column grid.
  for (const row of rows) {
    const offset = columns - row.length;
    for (let j = 0; j < row.length; j++) {
      const ch = row[j];
      if (ch === ' ') continue; // explicit spacer — keeps '+ 85' honest
      const cell: TextSpec = {
        kind: 'text',
        text: ch,
        x: colCenters[offset + j],
        y,
        fontSize,
        fontFamily: opts.fontFamily,
        fill: opts.color,
        originX: 'center',
        originY: 'top',
      };
      specs.push(cell);
    }
    y += boxH + rowGap;
  }
  y -= rowGap; // no gap after the final row

  if (rule) {
    y += fontSize * 0.12 + ruleWeight / 2;
    const sumLine: LineSpec = {
      kind: 'line',
      x1: 0,
      y1: y,
      x2: width,
      y2: y,
      stroke: opts.color,
      strokeWidth: ruleWeight,
      lineCap: 'butt',
    };
    specs.push(sumLine);
    y += ruleWeight / 2;
  }

  if (opts.answerBoxes) {
    y += fontSize * 0.14;
    const ansH = boxH;
    const ansW = colWidth * 0.86;
    for (let c = 0; c < columns; c++) {
      const box: RectSpec = {
        kind: 'rect',
        x: colCenters[c] - ansW / 2,
        y,
        width: ansW,
        height: ansH,
        fill: '',
        stroke: boxColor,
        strokeWidth: Math.max(1, fontSize * 0.025),
        rx: ansH * 0.08,
      };
      specs.push(box);
    }
    y += ansH;
  }

  return { specs, width, height: y, colCenters, columns };
}

// ---------------------------------------------------------------------------
// Brackets
// ---------------------------------------------------------------------------

export interface DivisionBracketOptions {
  /** Width of the dividend the bar must cover (recipe measures its text). */
  dividendWidth: number;
  /** Height of the bracket's upright (the dividend's text-box height). */
  height: number;
  color: string;
  strokeWidth: number;
  /** Horizontal reach of the curved upright. Default height * 0.42. */
  hook?: number;
  /** Bar overhang past the dividend's right edge. Default height * 0.12. */
  overhang?: number;
}

export interface DivisionBracketBlock extends KitBlock {
  /** Where the dividend's left edge belongs (right of the curve). */
  contentLeft: number;
}

/**
 * Long-division bracket ⟌ — one open path: a quadratic upright rising from
 * the baseline that meets a horizontal vinculum running over the dividend.
 * Stroke only (fill '') so it stays a line drawing at any weight; the bar
 * length is dividendWidth + overhang — the bar law again, division-flavored.
 */
export function divisionBracket(opts: DivisionBracketOptions): DivisionBracketBlock {
  const h = Math.max(1, opts.height);
  const hook = opts.hook ?? h * 0.42;
  const overhang = opts.overhang ?? h * 0.12;
  const width = hook + Math.max(0, opts.dividendWidth) + overhang;
  const fmt = (n: number): number => Math.round(n * 1000) / 1000;
  const spec: PathSpec = {
    kind: 'path',
    path: `M 0 ${fmt(h)} Q ${fmt(hook * 0.9)} ${fmt(h * 0.72)} ${fmt(hook)} 0 L ${fmt(width)} 0`,
    fill: '',
    stroke: opts.color,
    strokeWidth: opts.strokeWidth,
    lineCap: 'round',
    lineJoin: 'round',
  };
  return { specs: [spec], width, height: h, contentLeft: hook + h * 0.08 };
}

export type BracketGlyph = '(' | ')' | '[' | ']' | '{' | '}';

export interface GroupingBracketOptions {
  glyph: BracketGlyph;
  /** Content height the bracket must span — THE AUTO-HEIGHT LAW input. */
  height: number;
  color: string;
  /** Default height * 0.035, clamped ≥ 1.5. */
  strokeWidth?: number;
  /** Default height * 0.16, the classic display-math aspect. */
  width?: number;
}

/**
 * Sized grouping bracket — a bracket DRAWN to span any content height
 * (glyph-font brackets stop scaling gracefully past display sizes; drawn
 * ones are print-true at any poster scale). Open strokes, local box
 * width × height:
 *
 *   ( ) — one quadratic bow
 *   [ ] — three-segment square hook
 *   { } — two quadratic lobes meeting a center cusp
 */
export function groupingBracket(opts: GroupingBracketOptions): KitBlock {
  const h = Math.max(1, opts.height);
  const w = opts.width ?? h * 0.16;
  const sw = opts.strokeWidth ?? Math.max(1.5, h * 0.035);
  const fmt = (n: number): number => Math.round(n * 1000) / 1000;
  // All paths draw the LEFT-side glyph; right-side glyphs mirror x.
  const mirror = opts.glyph === ')' || opts.glyph === ']' || opts.glyph === '}';
  const X = (x: number): number => fmt(mirror ? w - x : x);

  let path: string;
  switch (opts.glyph) {
    case '(':
    case ')': {
      // Bow: ends at the right edge, bulging to the left edge at mid-height.
      path = `M ${X(w)} 0 Q ${X(0)} ${fmt(h / 2)} ${X(w)} ${fmt(h)}`;
      break;
    }
    case '[':
    case ']': {
      const lip = w * 0.85;
      path = `M ${X(lip)} 0 L ${X(0)} 0 L ${X(0)} ${fmt(h)} L ${X(lip)} ${fmt(h)}`;
      break;
    }
    case '{':
    case '}': {
      const mid = h / 2;
      const spine = w * 0.55;
      // Upper lobe → cusp → lower lobe. The cusp reaches the outer edge.
      path =
        `M ${X(w)} 0 Q ${X(spine)} 0 ${X(spine)} ${fmt(mid * 0.5)} ` +
        `L ${X(spine)} ${fmt(mid * 0.82)} Q ${X(spine)} ${fmt(mid)} ${X(0)} ${fmt(mid)} ` +
        `Q ${X(spine)} ${fmt(mid)} ${X(spine)} ${fmt(mid * 1.18)} ` +
        `L ${X(spine)} ${fmt(h - mid * 0.5)} Q ${X(spine)} ${fmt(h)} ${X(w)} ${fmt(h)}`;
      break;
    }
  }

  const spec: PathSpec = {
    kind: 'path',
    path,
    fill: '',
    stroke: opts.color,
    strokeWidth: sw,
    lineCap: 'round',
    lineJoin: 'round',
  };
  return { specs: [spec], width: w, height: h };
}
